import { memo, useMemo, useState } from 'react'

import { Stack, useTheme } from '@mui/material'

import { Icon } from '@iconify/react'
import { useTranslation } from 'react-i18next'
import { useDebounce } from '@uidotdev/usehooks'
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd'

import { useMentorCloud } from 'src/contexts/MentorCloudContext'

import useModal from 'src/hooks/useModal'
import { useMentorCloudMoveFile } from 'src/hooks/files/useMentorCloudMoveFile'

import { routePaths } from 'src/navigation/paths'

import BreadCrumbs from 'src/components/common/breadcrumbs'
import { MentorCloudUploaderModal } from 'src/components/mentor-cloud/MentorCloudUploader/MentorCloudUploader'

import { LOCAL_STORAGE_MENTOR_CLOUD_FILE_SORT, LOCAL_STORAGE_MENTOR_CLOUD_VIEW } from 'src/utils/mediaStorage/constants'

import { MentorCloudSidebar } from '../../../components/mentor-cloud/MentorCloudSidebar/MentorCloudSidebar'
import { MentorCloudHeader } from '../../../components/mentor-cloud/MentorCloudHeader/MentorCloudHeader'
import { MentorCloudInfiniteList } from '../../../components/mentor-cloud/MentorCloudInfiniteList/MentorCloudInfiniteList'
import { MentorCloudSearchBar } from '../../../components/common/mentor-cloud/MentorCloudSearchBar/MentorCloudSearchBar'

import { Folder, MentorCloudFileExtension } from 'src/types/files/file'
import { OrderConfig } from 'src/types/courses'

type MediaStorageProps = {
  type: MentorCloudFileExtension
  isModal?: boolean
}

export type FileSort = 'a-z' | 'z-a' | 'newest' | 'oldest' | 'largest' | 'smallest'

const sortFiles = (sort: FileSort): OrderConfig => {
  switch (sort) {
    case 'a-z':
      return { column: 'name', ascending: true }
    case 'z-a':
      return { column: 'name', ascending: false }
    case 'newest':
      return { column: 'created_at_epoch', ascending: false }
    case 'oldest':
      return { column: 'created_at_epoch', ascending: true }
    case 'largest':
      return { column: 'size', ascending: false }
    case 'smallest':
      return { column: 'size', ascending: true }
    default:
      return { column: 'name', ascending: true }
  }
}

const MentorCloud = memo(({ type, isModal }: MediaStorageProps) => {
  const { isOpen: isFileUploadOpen, open, close } = useModal()

  const { files, onChangeFiles } = useMentorCloud()

  const theme = useTheme()

  const { t } = useTranslation()

  const initialView = localStorage.getItem(LOCAL_STORAGE_MENTOR_CLOUD_VIEW) as 'grid' | 'list'
  const [view, setView] = useState<'grid' | 'list'>(initialView || 'grid')

  const initialSort = localStorage.getItem(LOCAL_STORAGE_MENTOR_CLOUD_FILE_SORT) as FileSort
  const [fileSort, setFileSort] = useState<FileSort>(initialSort || 'a-z')
  const [search, setSearch] = useState<string>('')

  const debouncedSearch = useDebounce(search, 500)

  const [activeFolder, setActiveFolder] = useState<Folder | null | undefined>()
  const [activeSubFolder, setActiveSubFolder] = useState<Folder | null | undefined>()

  const [movedFileId, setMovedFileId] = useState<number | null>(null)

  const onChangeView = (view: 'grid' | 'list') => {
    setView(view)
    localStorage.setItem(LOCAL_STORAGE_MENTOR_CLOUD_VIEW, view)
  }

  const onChangeSort = (sort: FileSort) => {
    setFileSort(sort)
    localStorage.setItem(LOCAL_STORAGE_MENTOR_CLOUD_FILE_SORT, sort)
  }

  const order = useMemo(() => sortFiles(fileSort), [fileSort])

  const { moveFile } = useMentorCloudMoveFile(
    { order, search: debouncedSearch, type },
    activeSubFolder?.id ?? activeFolder?.id
  )

  const onDragEnd = async (result: DropResult) => {
    const { destination, draggableId } = result

    if (!destination || !destination.droppableId.includes('folder')) return

    let folderId: string | number | null = ''

    folderId = destination.droppableId.replace('folder-', '')

    const fileId = Number(draggableId)

    folderId = folderId === 'null' ? null : Number(folderId)

    setMovedFileId(fileId)
    await moveFile({
      file: {
        id: fileId
      },
      destinationFolderId: folderId
    })
    setMovedFileId(null)
  }

  return (
    <Stack gap={theme.spacing(6)} height={'100%'} width={'100%'}>
      {!isModal && (
        <Stack direction={'row'} gap={theme.spacing(2)}>
          <BreadCrumbs
            separator={<Icon icon='tabler:chevron-right' />}
            data={[
              {
                href: routePaths.courses,
                text: t('PAGE.COURSE_OVERVIEW.course_overview')
              },
              {
                href: routePaths.mediaStorage,
                text: t('PAGE.MENTORCLOUD.title'),
                isActive: true
              }
            ]}
          />
        </Stack>
      )}
      <MentorCloudUploaderModal
        parentFolderId={activeSubFolder?.id || activeFolder?.id}
        isOpen={isFileUploadOpen}
        onClose={close}
      />
      <Stack
        bgcolor={theme.palette.background.paper}
        borderRadius={theme.spacing(1.5)}
        overflow={'hidden'}
        width={'100%'}
        height={'100%'}
        sx={{
          flexDirection: { xs: 'column', md: 'row' },
          overflowY: 'auto'
        }}
        boxShadow={theme.shadows[3]}
      >
        <DragDropContext onDragEnd={onDragEnd}>
          <MentorCloudSidebar
            activeFolder={activeFolder}
            onChangeActiveFolder={setActiveFolder}
            onChangeActiveSubFolder={setActiveSubFolder}
          />
          <Stack
            direction={'row'}
            width={'100%'}
            sx={{
              padding: { xs: theme.spacing(2), md: theme.spacing(4) }
            }}
          >
            <Stack gap={theme.spacing(4)} width={'100%'}>
              <MentorCloudHeader
                parentFolder={activeSubFolder ?? activeFolder}
                onClickUpload={open}
                parentFolderId={activeSubFolder?.id ?? activeFolder?.id}
              />
              <MentorCloudSearchBar
                sort={fileSort}
                onChangeSort={onChangeSort}
                view={view}
                onChangeView={onChangeView}
                search={search}
                onChangeSearch={setSearch}
              />
              <Droppable droppableId='wrapper' isDropDisabled>
                {droppable => (
                  <div ref={droppable.innerRef} {...droppable.droppableProps} style={{ display: 'contents' }}>
                    <MentorCloudInfiniteList
                      filters={{
                        order,
                        search: debouncedSearch,
                        type
                      }}
                      view={view}
                      isModal={isModal}
                      activeFile={files?.[0]}
                      movedFileId={movedFileId}
                      activeFolder={activeSubFolder ?? activeFolder}
                      onClickFile={file => onChangeFiles?.([file])}
                      onClickFolder={folder => {
                        setActiveSubFolder(folder)
                        setActiveFolder(folder)
                      }}
                    />
                  </div>
                )}
              </Droppable>
            </Stack>
          </Stack>
        </DragDropContext>
      </Stack>
    </Stack>
  )
})

export default MentorCloud
